// Google Drive integration - Shared token via Supabase, no per-user login
import { supabase } from './supabase';

const API_KEY       = process.env.REACT_APP_GOOGLE_API_KEY;
const CLIENT_ID     = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;
const ROOT_FOLDER_NAME = 'ARMSTECH ERP';

let lastDriveError = '';   // DRIVE-ERR
let cachedToken    = null;
let tokenExpiresAt = 0;

async function getSharedAccessToken() {
  if (cachedToken && Date.now() < tokenExpiresAt - 60000) return cachedToken;
  const { data, error } = await supabase.from('app_config').select('value').eq('key','google_drive_refresh_token').single();
  if (error || !data?.value) throw new Error('Drive token not found in the database' + (error ? ' (' + error.message + ')' : '') + '.');
  const res = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: CLIENT_ID, client_secret: CLIENT_SECRET, refresh_token: data.value, grant_type: 'refresh_token' }),
  }).catch(() => { throw new Error('Could not reach Google (network blocked or offline).'); });
  const tokens = await res.json();
  if (!tokens.access_token) throw new Error('Failed to get Drive access: ' + (tokens.error_description || tokens.error || 'unknown'));
  cachedToken    = tokens.access_token;
  tokenExpiresAt = Date.now() + (tokens.expires_in || 3600) * 1000;
  // Save token refresh timestamp so we can warn admin before expiry
  supabase.from('app_config').upsert({key:'google_drive_token_refreshed_at', value: new Date().toISOString()}).then(()=>{});
  return cachedToken;
}

export function getDriveError() { return lastDriveError; }

export async function isDriveConfigured() {
  try {
    if (!CLIENT_ID || !CLIENT_SECRET) throw new Error('Google client id/secret missing in the app settings (Vercel environment variables).');
    const t = await getSharedAccessToken();
    lastDriveError = '';
    return !!t;
  } catch (e) {
    lastDriveError = (e && e.message) || 'Unknown Drive error';
    console.error('Drive check failed:', lastDriveError);
    return false;
  }
}

async function findOrCreateFolder(name, parentId = null) {
  const token = await getSharedAccessToken();
  const query = parentId
    ? `name='${name}' and mimeType='application/vnd.google-apps.folder' and '${parentId}' in parents and trashed=false`
    : `name='${name}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
  const res  = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name)`, { headers: { Authorization: `Bearer ${token}` } });
  const data = await res.json();
  if (data.files?.length > 0) return data.files[0].id;
  const meta = { name, mimeType: 'application/vnd.google-apps.folder', ...(parentId ? { parents: [parentId] } : {}) };
  const cr = await fetch('https://www.googleapis.com/drive/v3/files', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(meta) });
  const folder = await cr.json();
  return folder.id;
}

export async function getStudentFolderId(studentName, registerNumber) {
  const rootId = await findOrCreateFolder(ROOT_FOLDER_NAME);
  return await findOrCreateFolder(`${registerNumber} - ${studentName}`, rootId);
}

export async function uploadFileToDrive(file, studentName, registerNumber, onProgress) {
  const token    = await getSharedAccessToken();
  const folderId = await getStudentFolderId(studentName, registerNumber);
  const form     = new FormData();
  form.append('metadata', new Blob([JSON.stringify({ name: file.name, parents: [folderId] })], { type: 'application/json' }));
  form.append('file', file);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink,size,mimeType,createdTime');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => { if (xhr.status === 200) resolve(JSON.parse(xhr.responseText)); else reject(new Error(`Upload failed: ${xhr.responseText}`)); };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(form);
  });
}

export async function listStudentFiles(studentName, registerNumber) {
  try {
    const token    = await getSharedAccessToken();
    const folderId = await getStudentFolderId(studentName, registerNumber);
    const res      = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(`'${folderId}' in parents and trashed=false`)}&fields=files(id,name,webViewLink,size,mimeType,createdTime)&orderBy=createdTime desc`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    return data.files || [];
  } catch { return []; }
}

export async function deleteFileFromDrive(fileId) {
  const token = await getSharedAccessToken();
  await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
}

export async function fetchImageAsBlob(fileId) {
  const token = await getSharedAccessToken();
  const res   = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Failed to fetch image');
  return URL.createObjectURL(await res.blob());
}

export async function loadGoogleApis() { return Promise.resolve(); }
export async function signInWithGoogle() { return getSharedAccessToken(); }
export function getAccessToken() { return cachedToken; }
export function setAccessToken(token) { cachedToken = token; }

export function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/1048576).toFixed(1)} MB`;
}

export function fileIcon(mimeType) {
  if (!mimeType) return '📄';
  if (mimeType.includes('pdf')) return '📕';
  if (mimeType.includes('image')) return '🖼️';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
  if (mimeType.includes('zip') || mimeType.includes('rar')) return '🗜️';
  return '📄';
}
